import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  HardHat,
  Phone,
} from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { StepList } from "@/components/site/Workflow";
import { SITE_IMG, STEPS } from "@/lib/siteImages";
import { siteImg, usePageHead } from "@/lib/siteContent";

const COUNT_ON = [
  "A single point of contact throughout the job",
  "Digital layout previews to align homeowners before install",
  "Expert input on seams, finishes and layout questions",
  "Fast answers when site conditions change unexpectedly",
  "Slab holds so the material is there when your cabinets are",
  "Same-day pricing on plans where we can",
];

const PACE = [
  {
    title: "Fast quotes",
    body: "Send plans or a sketch and we price it from real slab inventory — no waiting on a supplier to call back.",
  },
  {
    title: "Show up when we say",
    body: "Templating, fabrication and install are scheduled by us, so the dates we give you are dates we control.",
  },
  {
    title: "Finished work that stays finished",
    body: "Our own crew sets, seals and cleans. No third-party installers, no finger-pointing, a short punch list.",
  },
];

export default function SiteContractors() {
  const site = useSite();
  const shop = site?.shop || {};

  usePageHead({
    title: "Contractors",
    description:
      "Premium stone fabrication for contractors and builders. Fast quotes, one project lead, in-house templating, CNC fabrication and installation that keeps your job on schedule.",
  });

  return (
    <SiteShell site={site} testId="site-contractors">
      <section className="relative min-h-[56vh] overflow-hidden sm:min-h-[66vh] lg:min-h-[78vh]">
        <img
          src={siteImg(site?.config?.contractors?.image, SITE_IMG.fabrication)}
          alt="Stone fabrication shop"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/40" />
        <div className="relative flex min-h-[56vh] flex-col justify-end px-6 pb-12 pt-12 sm:min-h-[66vh] sm:pb-16 sm:pt-16 lg:min-h-[78vh] lg:px-12">
          <div className="s-micro flex items-center gap-2 text-white/70">
            <HardHat size={14} weight="bold" /> Trade · Contractors
          </div>
          <h1
            className="s-display mt-5 max-w-4xl text-4xl text-white sm:mt-6 sm:text-6xl lg:text-7xl"
            data-testid="contractors-headline"
          >
            Premium stone fabrication for excellence-driven contractors
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85 sm:text-xl">
            Fast quotes, full control and a dedicated team that delivers exactly
            what you need, when you need it.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/contact?topic=contractor"
              className="s-btn"
              data-testid="contractors-hero-cta"
            >
              Send us your plans
            </Link>
            <Link to="/inventory" className="s-ghost-light">
              What is on the racks
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--s-surface)] px-6 py-16 sm:py-24 lg:px-12 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <div>
            <div className="s-micro text-[color:var(--s-gold)]">
              We move at a contractor&apos;s pace
            </div>
            <h2 className="s-display mt-5 max-w-xl text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
              Get in, get it done right, keep the job on schedule
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg">
              You do not need hand-holding. You need a stone partner who quotes
              fast, shows up when we say we will and delivers finished work that
              does not need revisiting.
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]">
              Our goal: keep your crews moving, your homeowners happy and your
              punch list short.
            </p>
          </div>
          <div className="grid gap-8" data-testid="contractors-pace">
            {PACE.map((p, i) => (
              <div
                key={p.title}
                className="grid grid-cols-[3rem_1fr] gap-6 border-t border-[color:var(--s-line)] pt-6"
              >
                <span className="s-num text-2xl text-[color:var(--s-gold)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>
                  <span className="s-display block text-xl">{p.title}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-[color:var(--s-mute)]">
                    {p.body}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="grid gap-px bg-[color:var(--s-line)] lg:grid-cols-2"
        data-testid="contractors-support"
      >
        <div className="s-fig relative min-h-[40vh] overflow-hidden sm:min-h-[50vh] lg:min-h-[64vh]">
          <img
            src={SITE_IMG.polishing}
            alt="Polishing a stone edge"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center bg-[color:var(--s-bg)] px-6 py-14 sm:py-20 lg:px-16">
          <div className="s-micro text-[color:var(--s-gold)]">
            Dedicated support from start to finish
          </div>
          <h2 className="s-display mt-5 text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
            One project lead, not a handoff
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">
            From your first quote to final install you work with one person who
            knows your timeline, understands your scope and picks up the phone.
            We do not disappear once the stone is cut.
          </p>
          <p className="s-micro mt-8 text-[color:var(--s-mute)]">
            What you can count on
          </p>
          <ul className="mt-3" data-testid="contractors-count-on">
            {COUNT_ON.map((t) => (
              <li
                key={t}
                className="flex gap-3 border-b border-[color:var(--s-line)] py-3.5 text-sm text-[color:var(--s-ink)]"
              >
                <Check
                  size={15}
                  weight="bold"
                  className="mt-0.5 shrink-0 text-[color:var(--s-gold)]"
                />
                {t}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[color:var(--s-mute)]">
            Support that keeps your job moving, not waiting.
          </p>
        </div>
      </section>

      <section className="grid gap-px bg-[color:var(--s-line)] lg:grid-cols-2">
        <div className="flex flex-col justify-center bg-[color:var(--s-surface)] px-6 py-14 sm:py-20 lg:px-16">
          <div className="s-micro text-[color:var(--s-gold)]">
            One team. One job. No runaround.
          </div>
          <h2 className="s-display mt-5 text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
            Everything handled in-house
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">
            When the schedule is tight and every day matters, the last thing you
            need is a fabricator slowing things down. Laser templating, CNC
            fabrication and installation are all ours — no vendor delays, no
            miscommunication, just fast turnarounds and precise cuts.
          </p>
          <div className="mt-10">
            <StepList steps={STEPS} testId="contractors-steps" />
          </div>
        </div>
        <div className="s-fig relative min-h-[40vh] overflow-hidden sm:min-h-[50vh] lg:min-h-full">
          <img
            src={SITE_IMG.warehouse}
            alt="Slabs in the warehouse"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      </section>

      <section
        className="border-t border-[color:var(--s-line)] bg-[color:var(--s-bg)] px-6 py-16 sm:py-24 lg:px-12 lg:py-28"
        data-testid="contractors-trusted"
      >
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <div>
            <div className="s-micro text-[color:var(--s-gold)]">
              Trusted by builders
            </div>
            <h2 className="s-display mt-5 max-w-xl text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
              Precision that holds up under pressure
            </h2>
          </div>
          <div>
            <p className="text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg">
              From custom home builders to multi-family developers, contractors
              count on us for consistency, speed and craftsmanship. Whether it
              is one kitchen or 200 units, we deliver stonework that shows up on
              time, installs cleanly and elevates the entire build.
            </p>
            <Link
              to="/trade/multi-family-commercial"
              className="s-link mt-8 inline-flex"
              data-testid="contractors-commercial-link"
            >
              Multi-family &amp; commercial program{" "}
             
            </Link>
          </div>
        </div>
      </section>

      <section
        className="bg-[color:var(--s-ink)] px-6 py-16 text-white sm:py-24 lg:px-12 lg:py-28"
        data-testid="contractors-cta"
      >
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="s-micro text-[color:var(--s-gold)]">
              Ready to work together?
            </div>
            <h2 className="s-display mt-5 max-w-2xl text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
              Send us your plans, request a quote or schedule a visit.
              We&apos;re ready when you are.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact?topic=contractor"
              className="s-btn !bg-[color:var(--s-surface)] !text-[color:var(--s-ink)]"
              data-testid="contractors-partner-cta"
            >
              Partner with us today <ArrowUpRight size={13} weight="bold" />
            </Link>
            {shop.phone && (
              <a
                href={`tel:${shop.phone_link || shop.phone}`}
                className="s-ghost-light"
                data-testid="contractors-phone"
              >
                <Phone size={14} weight="bold" /> {shop.phone}
              </a>
            )}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
